import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, auditFieldsForUpdate, newId } from '../engine/context'
import { deleteRow, findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import {
  bigUnitNameOf,
  fail,
  handle,
  idOf,
  materialOf,
  nextDocNo,
  pad,
  parse,
  requireRole,
  round,
  statusCodeOf,
  stockMovement,
  txLog,
  workflowRunner
} from './c07.shared'
import { nextUnitCode } from './inboundRmOperator.handler'
import { bigQtyOf, rackCodeOf, reservedByUnit, stockUnits } from './materialRequest.handler'
import { notify, wmsRoute } from './notification.shared'
import { nextPrNo } from './wms.handler'

/**
 * Endpoint custom C07 Transfer antar gudang (WF-TRF-01, entity `transfer`) — TRF-01 / TRF-01A:
 * - `GET stock | plan | sji`; `POST create` (Admin Sansugi), `accept` (Admin WH RM + kekurangan stok),
 *   `assign` (bagi tugas picking → REQ-03 tab Transfer), `request-cancel` / `decide-cancel`,
 *   `issue-sji` (nomor `nnn/SJI/MM/YYYY`), `dispatch`, `receive` (per unit, parsial boleh).
 * Approval Supervisor/Kadiv + penolakan lewat `/api/workflow/submission/v2` standar (WorkflowStudio).
 * Leftover C07: "Terima + Buat PR Kekurangan" menerbitkan PR sungguhan (`shortage_pr_id`); pembatalan berlaku sejak
 * Pending Approval sampai Preparing (tugas belum selesai dibatalkan, unit terambil dikembalikan lewat tugas
 * Return-to-Stock REQ-03); penerimaan Sansugi membukukan stok ke WH-SGI (tanpa putaway).
 * Stok sumber = unit In Stock di rak − yang dipesan item picking Pending; staging selesai dari REQ-03 (`complete-task`).
 */

export const TRF_WORKFLOW_CODE = 'WF-TRF-01'
const STAGING_AREA = 'ZS-RM'
const runner = workflowRunner(TRF_WORKFLOW_CODE, 'transfer')

const linesOf = (transfer: LocalRow) =>
  readTable('transfer_line').filter(line => idOf(line.transfer_id) === transfer.id)
const tasksOf = (transfer: LocalRow) =>
  readTable('picking_task').filter(task => task.type === 'TRANSFER' && task.doc_ref === transfer.itr_no)
const itemsOfTransfer = (transfer: LocalRow) => {
  const taskIds = new Set(tasksOf(transfer).map(task => task.id))
  return readTable('picking_task_item').filter(item => taskIds.has(idOf(item.task_id)))
}

function requireTransfer(id: unknown) {
  const transfer = findRow('transfer', String(id ?? ''))
  if (!transfer) fail('Transfer tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  return transfer as LocalRow
}

function requireStatus(transfer: LocalRow, ...codes: string[]) {
  if (!codes.includes(statusCodeOf(transfer)))
    fail('Status transfer tidak sesuai untuk aksi ini.', 'INVALID_FROM_STATUS', 409)
}

/* ------------------------------------------------------------------ stok */

/** Saldo fisik (rak) dan yang bisa dialokasikan (fisik − dipesan tugas lain) per material, satuan besar. */
export function stock() {
  const reserved = reservedByUnit()
  const byMaterial = new Map<string, { physical: number; allocatable: number }>()
  readTable('rm_unit')
    .filter(unit => unit.unit_status === 'IN_STOCK' && unit.rack_bin_id)
    .forEach(unit => {
      const key = idOf(unit.material_id)
      const entry = byMaterial.get(key) ?? { physical: 0, allocatable: 0 }
      const physical = bigQtyOf(unit)
      entry.physical = round(entry.physical + physical)
      entry.allocatable = round(entry.allocatable + Math.max(0, physical - (reserved.get(unit.id) ?? 0)))
      byMaterial.set(key, entry)
    })
  return { items: [...byMaterial.entries()].map(([material_id, value]) => ({ material_id, ...value })) }
}

type Plan = {
  line_id: string
  material_id: string
  material_code: string
  material_name: string
  unit_name: string
  unit_id: string
  qr_code: string
  rack: string
  qty: number
  wip: boolean
}

/** Alokasi unit WIP-first FIFO untuk `qty_alloc` tiap baris (tidak menyimpan apa pun). */
function buildPlan(transfer: LocalRow) {
  const reserved = reservedByUnit()
  const taken = new Map<string, number>()
  const rows: Plan[] = []
  const short: { line_id: string; short: number }[] = []
  linesOf(transfer).forEach(line => {
    const material = materialOf(line.material_id) as LocalRow
    let need = Number(line.qty_alloc ?? line.qty_requested ?? 0)
    stockUnits(idOf(line.material_id), reserved).forEach(entry => {
      if (need <= 0) return
      const free = round(entry.avail - (taken.get(String(entry.unit.id)) ?? 0))
      const take = Math.min(free, need)
      if (take <= 0) return
      taken.set(String(entry.unit.id), round((taken.get(String(entry.unit.id)) ?? 0) + take))
      need = round(need - take)
      rows.push({
        line_id: String(line.id),
        material_id: String(material.id),
        material_code: String(material.code),
        material_name: String(material.name),
        unit_name: bigUnitNameOf(material),
        unit_id: String(entry.unit.id),
        qr_code: String(entry.unit.qr_code),
        rack: entry.rack,
        qty: take,
        wip: entry.wip
      })
    })
    if (need > 0) short.push({ line_id: String(line.id), short: need })
  })
  return { rows, short }
}

export function plan(transferId: unknown) {
  return buildPlan(requireTransfer(transferId))
}

/* --------------------------------------------------------- TRF-01 buat & terima */

type CreateBody = { lines?: { material_id: string; qty: number }[] }

export function createTransfer(body: CreateBody) {
  requireRole('Request transfer dibuat oleh Admin WH Sansugi.', 'admin_wh_sansugi')
  const merged = new Map<string, number>()
  ;(body.lines ?? []).forEach(line => {
    const qty = Number(line.qty ?? 0)
    if (!line.material_id || !(qty > 0)) return
    if (!materialOf(line.material_id)) fail('Material tidak ditemukan.', 'ERR_NOT_FOUND', 404)
    merged.set(line.material_id, round((merged.get(line.material_id) ?? 0) + qty))
  })
  if (!merged.size) fail('Minimal satu baris material.')
  const id = newId()
  const { statusId, submissionId } = runner.start(id)
  const transfer = insertRow('transfer', {
    ...auditFieldsForCreate(),
    id,
    itr_no: nextDocNo('ITR', 'transfer', 'itr_no'),
    direction: 'UTAMA→SGI',
    status_id: statusId,
    submission_id: submissionId,
    sji_no: null,
    departed_at: null,
    shortage_pr_id: null,
    cancel_requested: false,
    cancel_reason: null,
    cancel_requested_at: null,
    is_in_transit: false
  })
  merged.forEach((qty, materialId) =>
    insertRow('transfer_line', {
      ...auditFieldsForCreate(),
      id: newId(),
      transfer_id: id,
      material_id: materialId,
      unit_id: materialOf(materialId)?.default_purchase_unit_id ?? null,
      qty_requested: qty,
      qty_alloc: null,
      qty_received: 0
    })
  )
  runner.run(transfer, 'ACT_TRF_SEND', ['ACT_TRF_ADMIN_SGI'])
  txLog(
    'Transfer',
    'TRF-01',
    'transfer-dibuat',
    String(transfer.itr_no),
    `Request ${transfer.itr_no} (UTAMA→SGI) — ${merged.size} item`
  )
  return { id, itr_no: transfer.itr_no }
}

type AcceptBody = { transfer_id?: string; shortage_action?: 'PLAIN' | 'PR' }

/** Admin WH RM menerima: qty kirim = min(diminta, bisa dialokasikan); kekurangan wajib dikonfirmasi. */
export function acceptTransfer(body: AcceptBody) {
  requireRole('Transfer diterima oleh Admin WH RM.', 'admin_wh_rm')
  const transfer = requireTransfer(body.transfer_id)
  requireStatus(transfer, 'TRF_REQUESTED')
  const free = new Map(stock().items.map(item => [item.material_id, item.allocatable]))
  const lines = linesOf(transfer).map(line => {
    const materialId = idOf(line.material_id)
    const available = free.get(materialId) ?? 0
    const alloc = Math.max(0, Math.min(Number(line.qty_requested), available))
    free.set(materialId, round(available - alloc))
    return { line, alloc, short: round(Number(line.qty_requested) - alloc) }
  })
  const shortage = lines.filter(row => row.short > 0)
  if (shortage.length && !body.shortage_action)
    fail('Stok sumber kurang — konfirmasi terima dengan kekurangan.', 'ERR_SHORTAGE_CONFIRM', 409)
  lines.forEach(({ line, alloc, short }) =>
    updateRow('transfer_line', line.id, { qty_alloc: alloc, short_qty: short, ...auditFieldsForUpdate() })
  )
  const pr =
    body.shortage_action === 'PR' && shortage.length
      ? createShortagePr(
          transfer,
          shortage.map(row => ({ line: row.line, qty: row.short }))
        )
      : null
  updateRow('transfer', transfer.id, {
    shortage_pr_requested: body.shortage_action === 'PR',
    shortage_pr_id: pr?.id ?? null,
    ...auditFieldsForUpdate()
  })
  const result = runner.run(transfer, 'ACT_TRF_ADMIN_ACCEPT', ['ACT_TRF_ADMIN_RM'])
  txLog(
    'Transfer',
    'TRF-01A',
    'transfer-diterima',
    String(transfer.itr_no),
    `Permintaan diterima${shortage.length ? ` — ${shortage.length} item kurang stok${pr ? ` (PR kekurangan ${pr.pr_no} terbit)` : ''}` : ''}`
  )
  return {
    ...result,
    pr_no: pr?.pr_no ?? null,
    shortage: shortage.map(row => ({ line_id: row.line.id, short: row.short, alloc: row.alloc }))
  }
}

/** PR kekurangan stok sumber atas nama WH Utama: supplier = pasangan material × supplier dengan lead time terpendek. */
const prFlow = workflowRunner('WF-PRC-01', 'purchase_request')
function createShortagePr(transfer: LocalRow, rows: { line: LocalRow; qty: number }[]) {
  const prId = newId()
  const { statusId, submissionId } = prFlow.start(prId)
  const pr = insertRow('purchase_request', {
    ...auditFieldsForCreate(),
    id: prId,
    pr_no: nextPrNo(),
    status_id: statusId,
    submission_id: submissionId,
    stage: null,
    rejection_reason: null
  })
  const fallbackSupplier = readTable('mt_companies').find(company => company.type === 'supplier')
  rows.forEach(({ line, qty }) => {
    const material = materialOf(line.material_id) as LocalRow
    const pack = readTable('mt_material_pack_standard')
      .filter(row => idOf(row.material_id) === material.id)
      .sort((a, b) => Number(a.lead_days ?? 0) - Number(b.lead_days ?? 0))[0]
    insertRow('purchase_request_line', {
      ...auditFieldsForCreate(),
      id: newId(),
      pr_id: prId,
      material_id: material.id,
      qty,
      unit_id: idOf(line.unit_id) || idOf(material.default_purchase_unit_id) || null,
      supplier_id: idOf(pack?.supplier_id) || idOf(fallbackSupplier) || null,
      remark: `Kekurangan stok transfer ${transfer.itr_no}`,
      source: 'TRANSFER_SHORTAGE',
      source_ref_id: line.id
    })
  })
  prFlow.run(pr, 'ACT_PRC_SUBMIT', ['ACT_PRC_ADMIN_RM'])
  return pr
}

/* ------------------------------------------------------- TRF-01A tugaskan operator */

type AssignBody = { transfer_id?: string; split?: { operator_id: string; count: number }[] }

export function assignTransfer(body: AssignBody) {
  requireRole('Tugas pengambilan dibagi oleh Admin WH RM.', 'admin_wh_rm')
  const transfer = requireTransfer(body.transfer_id)
  requireStatus(transfer, 'TRF_APPROVED')
  if (transfer.cancel_requested === true) fail('Pembatalan sedang diajukan — putuskan dulu pembatalannya.')
  const { rows, short } = buildPlan(transfer)
  if (!rows.length) fail('Tidak ada unit di rak yang bisa dialokasikan untuk transfer ini.')
  if (short.length) fail('Stok sumber berubah — alokasi kurang dari qty kirim. Muat ulang halaman.')
  const split = (body.split ?? []).filter(part => Number(part.count) > 0)
  const total = split.reduce((sum, part) => sum + Number(part.count), 0)
  if (!split.length || total !== rows.length) fail(`Jumlah unit yang dibagi harus ${rows.length}.`)
  split.forEach(part => {
    const operator = findRow('directus_users', part.operator_id)
    const roles = readTable('mt_privileges')
      .filter(row => idOf(row.user) === part.operator_id)
      .map(row => String(findRow('mt_roles', idOf(row.role))?.code ?? ''))
    if (!operator || !roles.includes('operator_wh_rm')) fail('Pilih operator tujuan dulu.')
  })
  const created: string[] = []
  let cursor = 0
  split.forEach(part => {
    const task = insertRow('picking_task', {
      ...auditFieldsForCreate(),
      id: newId(),
      pick_no: nextDocNo('PICK', 'picking_task', 'pick_no'),
      type: 'TRANSFER',
      doc_ref: transfer.itr_no,
      operator_id: part.operator_id,
      task_status: 'ASSIGNED',
      machine_id: null,
      area_code: STAGING_AREA
    })
    rows.slice(cursor, cursor + Number(part.count)).forEach(row => {
      const unit = findRow('rm_unit', row.unit_id) as LocalRow
      insertRow('picking_task_item', {
        ...auditFieldsForCreate(),
        id: newId(),
        task_id: task.id,
        unit_id: row.unit_id,
        line_id: null,
        transfer_line_id: row.line_id,
        unit_name: row.unit_name,
        qty_plan: row.qty,
        qty_taken: 0,
        from_rack_bin_id: unit.rack_bin_id,
        from_warehouse_zone_id: unit.warehouse_zone_id ?? null,
        is_wip: row.wip,
        picked_at: null,
        handover_id: null,
        status: 'PENDING'
      })
    })
    cursor += Number(part.count)
    created.push(String(task.pick_no))
  })
  const result = runner.run(transfer, 'ACT_TRF_ASSIGN', ['ACT_TRF_ADMIN_RM'])
  txLog(
    'Transfer',
    'TRF-01A',
    'tugas-dibuat',
    String(transfer.itr_no),
    `Tugas pengambilan dibuat: ${created.join(', ')} (${rows.length} unit)`
  )
  /** Tugas pengambilan transfer → tiap operator yang dibagi unit mendapat notifikasi. */
  split.forEach(part =>
    notify({
      to: { userIds: [part.operator_id] },
      type: 'PICKING_ASSIGNED',
      title: `Tugas pengambilan transfer ${transfer.itr_no}`,
      body: `${part.count} unit perlu diambil untuk transfer ${transfer.itr_no} — buka Picking — Operator.`,
      link: wmsRoute('request-production/picking-rm-operator', { tab: 'transfer' }),
      ref: { type: 'transfer', id: String(transfer.id) }
    })
  )
  return { ...result, tasks: created, units: rows.length }
}

/* ------------------------------------------------------------- pembatalan */

/** Status yang boleh memicu pengajuan pembatalan Sansugi (prototype trf-01a `berjalan`) → aksi pembatalan disetujui. */
const CANCEL_ACTION: Record<string, string> = {
  TRF_PENDING_SPV: 'ACT_TRF_CANCEL_APPROVED_SPV',
  TRF_PENDING_KADIV: 'ACT_TRF_CANCEL_APPROVED_KADIV',
  TRF_APPROVED: 'ACT_TRF_CANCEL_APPROVED',
  TRF_PREPARING: 'ACT_TRF_CANCEL_APPROVED_PREP'
}
const CANCELLABLE = Object.keys(CANCEL_ACTION)

export function requestCancel(body: { transfer_id?: string; reason?: string }) {
  requireRole('Pembatalan diajukan oleh Admin WH Sansugi.', 'admin_wh_sansugi')
  const transfer = requireTransfer(body.transfer_id)
  requireStatus(transfer, ...CANCELLABLE)
  const reason = String(body.reason ?? '').trim()
  if (!reason) fail('Alasan wajib diisi.')
  if (transfer.cancel_requested === true) fail('Pembatalan sudah diajukan.')
  updateRow('transfer', transfer.id, {
    cancel_requested: true,
    cancel_reason: reason,
    cancel_requested_at: new Date().toISOString(),
    cancel_rejected_reason: null,
    ...auditFieldsForUpdate()
  })
  txLog('Transfer', 'TRF-01A', 'pembatalan-diajukan', String(transfer.itr_no), `Pembatalan diajukan — ${reason}`)
  return { id: transfer.id }
}

/**
 * Pembatalan disetujui: tugas pengambilan yang belum selesai dibatalkan (item Pending dihapus → alokasi rak lepas),
 * unit yang sudah terambil / berdiri di staging ZS-RM kembali ke rak lewat tugas Return-to-Stock (REQ-03 tab Return).
 */
export function cancelTransferWork(transfer: LocalRow) {
  const returns = new Map<string, LocalRow[]>()
  let dropped = 0
  tasksOf(transfer).forEach(task => {
    const items = readTable('picking_task_item').filter(item => idOf(item.task_id) === task.id)
    items.forEach(item => {
      if (item.status === 'PENDING') {
        deleteRow('picking_task_item', item.id)
        dropped += 1
      } else if (item.status === 'PICKED') {
        const operator = idOf(task.operator_id)
        returns.set(operator, [...(returns.get(operator) ?? []), item])
      }
    })
    const left = readTable('picking_task_item').filter(item => idOf(item.task_id) === task.id)
    if (!left.length) deleteRow('picking_task', task.id)
    else updateRow('picking_task', task.id, { task_status: 'COMPLETED', ...auditFieldsForUpdate() })
  })
  const created: string[] = []
  returns.forEach((origins, operatorId) => {
    const returnTask = insertRow('picking_task', {
      ...auditFieldsForCreate(),
      id: newId(),
      pick_no: nextDocNo('PICK', 'picking_task', 'pick_no'),
      type: 'RETURN',
      doc_ref: transfer.itr_no,
      operator_id: operatorId,
      task_status: 'ASSIGNED',
      machine_id: null,
      area_code: null
    })
    created.push(String(returnTask.pick_no))
    origins.forEach(origin =>
      insertRow('picking_task_item', {
        ...auditFieldsForCreate(),
        id: newId(),
        task_id: returnTask.id,
        unit_id: origin.unit_id,
        line_id: null,
        transfer_line_id: origin.transfer_line_id ?? null,
        unit_name: origin.unit_name ?? null,
        qty_plan: round(Number(origin.qty_taken ?? 0) - Number(origin.qty_returned ?? 0)),
        qty_taken: 0,
        from_rack_bin_id: origin.from_rack_bin_id,
        from_warehouse_zone_id: origin.from_warehouse_zone_id ?? null,
        is_wip: origin.is_wip === true,
        return_of: origin.id,
        picked_at: null,
        handover_id: null,
        status: 'PENDING'
      })
    )
  })
  return { dropped, returnTasks: created, returnUnits: [...returns.values()].flat().length }
}

export function decideCancel(body: { transfer_id?: string; approve?: boolean; reason?: string }) {
  requireRole('Pembatalan diputuskan oleh Admin WH RM.', 'admin_wh_rm')
  const transfer = requireTransfer(body.transfer_id)
  requireStatus(transfer, ...CANCELLABLE)
  if (transfer.cancel_requested !== true) fail('Tidak ada pengajuan pembatalan.')
  const reason = String(body.reason ?? '').trim()
  if (body.approve) {
    const action = CANCEL_ACTION[statusCodeOf(transfer)]
    runner.run(transfer, action, ['ACT_TRF_ADMIN_RM'], {}, String(transfer.cancel_reason ?? 'Pembatalan disetujui'))
    const work = cancelTransferWork(transfer)
    updateRow('transfer', transfer.id, { cancel_requested: false, ...auditFieldsForUpdate() })
    txLog(
      'Transfer',
      'TRF-01A',
      'pembatalan-disetujui',
      String(transfer.itr_no),
      `Pembatalan disetujui — transfer dibatalkan${work.dropped ? `, ${work.dropped} unit belum diambil dilepas` : ''}${work.returnTasks.length ? `, ${work.returnUnits} unit dikembalikan ke rak (${work.returnTasks.join(', ')})` : ''}`
    )
    return { cancelled: true, return_tasks: work.returnTasks, released: work.dropped }
  }
  if (!reason) fail('Alasan wajib diisi.')
  updateRow('transfer', transfer.id, {
    cancel_requested: false,
    cancel_rejected_reason: reason,
    ...auditFieldsForUpdate()
  })
  txLog('Transfer', 'TRF-01A', 'pembatalan-ditolak', String(transfer.itr_no), `Pembatalan ditolak — ${reason}`)
  return { cancelled: false }
}

/* ------------------------------------------------------- SJI, kirim, terima */

export function sjiItems(transfer: LocalRow) {
  return itemsOfTransfer(transfer)
    .filter(item => item.status === 'PICKED')
    .map(item => {
      const unit = findRow('rm_unit', idOf(item.unit_id)) as LocalRow
      const material = materialOf(unit.material_id) as LocalRow
      return {
        item_id: String(item.id),
        unit_id: String(unit.id),
        qr_code: String(unit.qr_code),
        batch_no: String(unit.batch_no ?? ''),
        material_code: String(material.code),
        material_name: String(material.name),
        unit_name: String(item.unit_name ?? bigUnitNameOf(material)),
        qty: Number(item.qty_taken ?? 0),
        qty_received: Number(item.qty_received ?? 0),
        rack: rackCodeOf(item.from_rack_bin_id)
      }
    })
}

export const sji = (transferId: unknown) => ({ items: sjiItems(requireTransfer(transferId)) })

export function issueSji(body: { transfer_id?: string; departed_at?: string }) {
  requireRole('Surat Jalan diterbitkan oleh Admin WH RM.', 'admin_wh_rm')
  const transfer = requireTransfer(body.transfer_id)
  requireStatus(transfer, 'TRF_READY_SHIP')
  if (transfer.sji_no) fail('Surat Jalan sudah terbit.')
  const departed = String(body.departed_at ?? '').trim()
  if (!/^\d{4}-\d{2}-\d{2}$/.test(departed)) fail('Isi tanggal berangkat dulu.')
  const items = sjiItems(transfer)
  if (!items.length) fail('Tidak ada unit hasil pengambilan yang tercatat pada transfer ini.')
  const [year, month] = departed.split('-')
  const tail = `/SJI/${month}/${year}`
  const highest = readTable('transfer')
    .map(row => String(row.sji_no ?? ''))
    .filter(no => no.endsWith(tail))
    .reduce((max, no) => Math.max(max, Number(no.slice(0, 3))), 0)
  const sjiNo = `${pad(highest + 1, 3)}${tail}`
  updateRow('transfer', transfer.id, { sji_no: sjiNo, departed_at: departed, ...auditFieldsForUpdate() })
  txLog(
    'Transfer',
    'TRF-01A',
    'sji-terbit',
    String(transfer.itr_no),
    `Surat Jalan ${sjiNo} terbit — berangkat ${departed} (${items.length} unit)`
  )
  return { sji_no: sjiNo, departed_at: departed, items }
}

export function dispatchTransfer(body: { transfer_id?: string }) {
  requireRole('Barang ditandai berangkat oleh Admin WH RM.', 'admin_wh_rm')
  const transfer = requireTransfer(body.transfer_id)
  requireStatus(transfer, 'TRF_READY_SHIP')
  if (!transfer.sji_no) fail('Terbitkan Surat Jalan dulu.')
  const result = runner.run(transfer, 'ACT_TRF_DISPATCH', ['ACT_TRF_ADMIN_RM'])
  itemsOfTransfer(transfer)
    .filter(item => item.status === 'PICKED')
    .forEach(item => {
      const unit = findRow('rm_unit', idOf(item.unit_id)) as LocalRow
      const perBig = Number(unit.small_per_big_snapshot ?? 1)
      if (unit.unit_status === 'IN_TRANSFER_STAGING')
        updateRow('rm_unit', unit.id, { unit_status: 'IN_TRANSIT', warehouse_zone_id: null, ...auditFieldsForUpdate() })
      stockMovement(
        'TRANSFER_OUT',
        unit,
        round(Number(item.qty_taken ?? 0) * perBig),
        null,
        null,
        String(transfer.sji_no)
      )
    })
  txLog(
    'Transfer',
    'TRF-01A',
    'barang-dikirim',
    String(transfer.itr_no),
    `Barang berangkat dengan Surat Jalan ${transfer.sji_no}`
  )
  return result
}

const SGI_ZONE_CODE = 'SGI-STORAGE'

/**
 * Stok masuk WH-SGI sebanyak yang diterima (BA TRF-01A: "stoknya berpindah ke WH-SGI … tidak ada QC ulang dan tidak ada
 * putaway di Sansugi"). Tumpukan penuh memakai ulang unit aslinya (stiker QR sama); tumpukan yang diambil sebagian
 * dibukukan sebagai unit baru dengan batch yang sama. Unit Sansugi berstatus In Stock tanpa posisi rak.
 */
function postSansugiStock(item: LocalRow, unit: LocalRow, qty: number) {
  const zone = readTable('warehouse_zone').find(row => row.code === SGI_ZONE_CODE)
  const perBig = Number(unit.small_per_big_snapshot ?? 1)
  const existing = item.sgi_unit_id ? findRow('rm_unit', idOf(item.sgi_unit_id)) : undefined
  if (existing) {
    updateRow('rm_unit', existing.id, {
      qty_small: round(Number(existing.qty_small ?? 0) + qty * perBig),
      ...auditFieldsForUpdate()
    })
    return existing
  }
  if (unit.unit_status === 'IN_TRANSIT') {
    updateRow('rm_unit', unit.id, {
      unit_status: 'IN_STOCK',
      qty_small: round(qty * perBig),
      rack_bin_id: null,
      warehouse_zone_id: zone?.id ?? null,
      stored_at: new Date().toISOString(),
      ...auditFieldsForUpdate()
    })
    updateRow('picking_task_item', item.id, { sgi_unit_id: unit.id, ...auditFieldsForUpdate() })
    return findRow('rm_unit', unit.id) as LocalRow
  }
  const created = insertRow('rm_unit', {
    ...auditFieldsForCreate(),
    id: newId(),
    uid: `UNIT-${pad(readTable('rm_unit').length + 1, 6)}`,
    qr_code: nextUnitCode(String(unit.batch_no ?? '')),
    material_id: unit.material_id,
    batch_no: unit.batch_no,
    supplier_id: unit.supplier_id ?? null,
    po_no: unit.po_no ?? null,
    qty_small: round(qty * perBig),
    rack_bin_id: null,
    warehouse_zone_id: zone?.id ?? null,
    flag: unit.flag ?? 'REGULAR',
    unit_status: 'IN_STOCK',
    qc_date: unit.qc_date ?? null,
    unit_big_snapshot: unit.unit_big_snapshot,
    small_per_big_snapshot: perBig,
    is_leftover_wip: unit.is_leftover_wip === true,
    stored_at: new Date().toISOString()
  })
  updateRow('picking_task_item', item.id, { sgi_unit_id: created.id, ...auditFieldsForUpdate() })
  return created
}

type ReceiveBody = { transfer_id?: string; units?: { item_id: string; qty: number }[] }

export function receiveTransfer(body: ReceiveBody) {
  requireRole('Barang diterima oleh Admin WH Sansugi.', 'admin_wh_sansugi')
  const transfer = requireTransfer(body.transfer_id)
  requireStatus(transfer, 'TRF_IN_TRANSIT', 'TRF_PARTIAL')
  const items = itemsOfTransfer(transfer).filter(item => item.status === 'PICKED')
  const entries = (body.units ?? []).map(entry => {
    const item = items.find(row => row.id === entry.item_id)
    if (!item) fail('Unit bukan bagian dari transfer ini.', 'ERR_NOT_FOUND', 404)
    const qty = Number(entry.qty ?? 0)
    const outstanding = round(Number(item.qty_taken ?? 0) - Number(item.qty_received ?? 0))
    if (!(qty >= 0) || qty > outstanding) fail(`Jumlah diterima harus antara 0 dan ${outstanding}.`)
    return { item: item as LocalRow, qty }
  })
  if (!entries.some(entry => entry.qty > 0)) fail('Isi dulu jumlah yang diterima — tidak boleh nol semua.')
  entries.forEach(({ item, qty }) => {
    if (qty <= 0) return
    const unit = findRow('rm_unit', idOf(item.unit_id)) as LocalRow
    updateRow('picking_task_item', item.id, {
      qty_received: round(Number(item.qty_received ?? 0) + qty),
      ...auditFieldsForUpdate()
    })
    const arrived = postSansugiStock(item, unit, qty)
    stockMovement(
      'TRANSFER_IN',
      arrived,
      round(qty * Number(unit.small_per_big_snapshot ?? 1)),
      null,
      null,
      String(transfer.sji_no)
    )
  })
  const refreshed = itemsOfTransfer(transfer).filter(item => item.status === 'PICKED')
  linesOf(transfer).forEach(line =>
    updateRow('transfer_line', line.id, {
      qty_received: round(
        refreshed
          .filter(item => idOf(item.transfer_line_id) === line.id)
          .reduce((sum, item) => sum + Number(item.qty_received ?? 0), 0)
      ),
      ...auditFieldsForUpdate()
    })
  )
  const complete = refreshed.every(item => Number(item.qty_received ?? 0) >= Number(item.qty_taken ?? 0))
  const status = statusCodeOf(transfer)
  let result: unknown = null
  if (complete) {
    result = runner.run(transfer, status === 'TRF_PARTIAL' ? 'ACT_TRF_RECEIVE_REMAINDER' : 'ACT_TRF_RECEIVE_FULL', [
      'ACT_TRF_ADMIN_SGI'
    ])
  } else if (status === 'TRF_IN_TRANSIT') {
    result = runner.run(transfer, 'ACT_TRF_RECEIVE_PART', ['ACT_TRF_ADMIN_SGI'], {}, 'Diterima sebagian')
  }
  const total = entries.reduce((sum, entry) => sum + entry.qty, 0)
  txLog(
    'Transfer',
    'TRF-01A',
    complete ? 'barang-diterima' : 'barang-diterima-sebagian',
    String(transfer.itr_no),
    `Barang diterima ${total} (${complete ? 'lengkap' : 'sebagian — transfer tetap terbuka'})`
  )
  return { complete, result }
}

/* ------------------------------------------------------------- registrasi */

export function registerTransferHandlers(mock: MockAdapter) {
  const base = '/api/v1/wms/transfer'
  mock.onGet(`${base}/stock`).reply(() => handle(() => stock()))
  mock.onGet(`${base}/plan`).reply(config => handle(() => plan(config.params?.transfer_id)))
  mock.onGet(`${base}/sji`).reply(config => handle(() => sji(config.params?.transfer_id)))
  mock.onPost(`${base}/create`).reply(config => handle(() => createTransfer(parse(config.data))))
  mock.onPost(`${base}/accept`).reply(config => handle(() => acceptTransfer(parse(config.data))))
  mock.onPost(`${base}/assign`).reply(config => handle(() => assignTransfer(parse(config.data))))
  mock.onPost(`${base}/request-cancel`).reply(config => handle(() => requestCancel(parse(config.data))))
  mock.onPost(`${base}/decide-cancel`).reply(config => handle(() => decideCancel(parse(config.data))))
  mock.onPost(`${base}/issue-sji`).reply(config => handle(() => issueSji(parse(config.data))))
  mock.onPost(`${base}/dispatch`).reply(config => handle(() => dispatchTransfer(parse(config.data))))
  mock.onPost(`${base}/receive`).reply(config => handle(() => receiveTransfer(parse(config.data))))
}
